import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  FaMicrophone,
  FaTrophy,
  FaUsers,
  FaClipboardList,
  FaIndustry,
  FaArrowRight,
  FaFilePdf,
  FaDownload,
  FaAtom,
  FaRegCalendarAlt,
  FaRegClock,
  FaUniversity,
  FaMapMarkerAlt,
  FaMedal,
  FaBullseye,
  FaMicrochip,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaFlask,
} from 'react-icons/fa';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import {
  Container,
  Section,
  Kicker,
  SectionTitle,
  Lead,
  ButtonPrimary,
  ButtonGhost,
  HudArrowButton,
  Reveal,
  Marquee,
  useCountUp,
  DotLattice,
  CopperGlow,
  Orbital3D,
} from '../components/ui';
import Countdown from '../components/Countdown';
import { CONFERENCE, STATS, HIGHLIGHTS, TOPICS } from '../data/conference';

/* ============================== HERO ============================== */

const scrollPulse = keyframes`
  0% { transform: translateY(0); opacity: 1; }
  70% { transform: translateY(10px); opacity: 0; }
  100% { transform: translateY(0); opacity: 0; }
`;

const Hero = styled.header`
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 7.5rem 0 4rem;

  /* fine engineering grid */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(34, 31, 26, 0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(34, 31, 26, 0.035) 1px, transparent 1px);
    background-size: 72px 72px;
    mask-image: radial-gradient(ellipse 90% 80% at 50% 30%, black 20%, transparent 80%);
    -webkit-mask-image: radial-gradient(ellipse 90% 80% at 50% 30%, black 20%, transparent 80%);
    pointer-events: none;
  }
`;

/* IEM Kolkata building — atmospheric photo overlay behind the hero */
const HeroPhoto = styled.div`
  position: absolute;
  inset: 0;
  background-image: linear-gradient(180deg, rgba(236, 233, 225, 0.72) 0%, rgba(236, 233, 225, 0.9) 68%, var(--bg) 100%),
    url('/assets/guru.png');
  background-size: cover;
  background-position: center 32%;
  opacity: 0.5;
  filter: grayscale(30%) sepia(14%) brightness(1.05) contrast(0.96);
  mask-image: linear-gradient(180deg, black 35%, transparent 96%);
  -webkit-mask-image: linear-gradient(180deg, black 35%, transparent 96%);
  pointer-events: none;
`;

const HeroInner = styled(Container)`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.9rem;
`;

const HeroKicker = styled.p`
  font-family: var(--font-mono);
  font-size: clamp(0.7rem, 1.4vw, 0.85rem);
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--ink-dim);
  max-width: 100%;

  @media (max-width: 560px) {
    letter-spacing: 0.16em;
  }

  b {
    color: var(--copper);
    font-weight: 600;
  }
`;

const HeroTitle = styled.h1`
  /* "IEMENTech '27" measures ~12em in Syne 800 — keep width under the container */
  font-size: min(7.4vw, 5.9rem);
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 0.95;
  white-space: nowrap;

  @media (max-width: 640px) {
    /* intentional two-line stack on small screens */
    white-space: normal;
    font-size: clamp(2.1rem, 9.5vw, 3.4rem);

    .year {
      display: block;
      margin-top: 0.1em;
    }
  }

  .year {
    background: linear-gradient(115deg, var(--copper-bright) 5%, var(--copper) 50%, var(--copper-deep) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.6rem 2.2rem;
  font-family: var(--font-mono);
  font-size: clamp(0.72rem, 1.4vw, 0.85rem);
  letter-spacing: 0.1em;

  span.tag {
    color: var(--copper-deep);
  }

  span.val {
    color: var(--ink);
    font-weight: 500;
  }
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 0.4rem;
`;

const OrganizerNote = styled.p`
  font-size: 0.88rem;
  color: var(--ink-faint);
  max-width: 64ch;
  line-height: 1.7;

  strong {
    color: var(--ink-dim);
    font-weight: 500;
  }
`;

const ScrollCue = styled.div`
  position: absolute;
  bottom: 1.6rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--font-mono);
  font-size: 0.62rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--ink-faint);

  .line {
    width: 1px;
    height: 34px;
    background: var(--line-strong);
    position: relative;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 12px;
      background: var(--copper);
      animation: ${scrollPulse} 1.8s ease-in-out infinite;
    }
  }
`;

/* ============================ OVERVIEW ============================ */

const OverviewGrid = styled.div`
  display: grid;
  /* minmax(0, …) stops intrinsic content from inflating the columns */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  gap: clamp(2.5rem, 6vw, 4.5rem);
  align-items: center;

  @media (max-width: 980px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const InfoPanel = styled.div`
  display: flex;
  gap: 1.3rem;
  align-items: flex-start;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-sm);
  background: rgba(255, 255, 255, 0.5);
  padding: 1.4rem 1.5rem;
  margin-top: 1.6rem;

  .sigil {
    flex: none;
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.6rem;
    color: var(--copper-bright);
    filter: drop-shadow(0 0 10px rgba(242, 176, 94, 0.45));
  }

  p {
    color: var(--ink-dim);
    font-size: 0.98rem;
    line-height: 1.75;
  }
`;

const FactStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-sm);
  background: rgba(255, 255, 255, 0.5);
  margin-top: 1.1rem;

  .fact {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    padding: 1rem 1.05rem;
    position: relative;

    & + .fact::before {
      content: '';
      position: absolute;
      left: 0;
      top: 22%;
      bottom: 22%;
      width: 1px;
      background: var(--line);
    }

    svg {
      flex: none;
      font-size: 1.15rem;
      color: var(--copper-bright);
    }

    b {
      display: block;
      font-family: var(--font-mono);
      font-size: 0.86rem;
      font-weight: 600;
      color: var(--ink);
      line-height: 1.25;
      white-space: nowrap;
    }

    i {
      display: block;
      font-style: normal;
      font-family: var(--font-mono);
      font-size: 0.56rem;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--ink-faint);
      margin-top: 0.15rem;
      white-space: nowrap;
    }
  }

  @media (max-width: 1240px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    .fact:nth-child(3)::before {
      display: none;
    }
  }

  /* let labels wrap on small screens instead of widening the page */
  @media (max-width: 600px) {
    .fact {
      padding: 0.85rem 0.8rem;

      b,
      i {
        white-space: normal;
      }
    }
  }
`;

/* --- tilted HUD screen (right column) --------------------------- */
const ScreenScene = styled.div`
  perspective: 1500px;
  position: relative;
  padding-bottom: 2.5rem;

  /* elliptical orbit rings under the screen */
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -8px;
    transform: translateX(-50%);
    border: 1px solid rgba(224, 141, 60, 0.28);
    border-radius: 50%;
    pointer-events: none;
  }

  &::before {
    width: min(112%, calc(100vw - 16px));
    height: 130px;
  }

  &::after {
    width: 86%;
    height: 92px;
    bottom: 6px;
    border-color: rgba(224, 141, 60, 0.16);
    box-shadow: 0 30px 50px -18px rgba(224, 141, 60, 0.22);
  }
`;

const Screen = styled.figure`
  --cut: 24px;
  position: relative;
  isolation: isolate;
  margin: 0;
  padding: 0.9rem;
  background: linear-gradient(
    110deg,
    rgba(126, 132, 138, 0.55) 0%,
    rgba(44, 49, 56, 0.95) 16%,
    rgba(224, 141, 60, 0.9) 48%,
    rgba(42, 47, 54, 0.96) 80%,
    rgba(126, 132, 138, 0.55) 100%
  );
  clip-path: polygon(
    var(--cut) 0,
    calc(100% - var(--cut)) 0,
    100% var(--cut),
    100% calc(100% - var(--cut)),
    calc(100% - var(--cut)) 100%,
    var(--cut) 100%,
    0 calc(100% - var(--cut)),
    0 var(--cut)
  );
  transform: rotateY(-9deg) rotateX(1.5deg);
  transform-style: preserve-3d;
  transition: transform 0.6s var(--ease-out);
  filter: drop-shadow(0 26px 44px rgba(0, 0, 0, 0.5)) drop-shadow(0 0 22px rgba(224, 141, 60, 0.14));

  &:hover {
    transform: rotateY(-4deg) rotateX(0.5deg);
  }

  &::before {
    content: '';
    position: absolute;
    inset: 2px;
    z-index: -1;
    background: linear-gradient(180deg, #f4f2ec, #ffffff);
    clip-path: inherit;
  }

  .bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: var(--font-mono);
    font-size: 0.66rem;
    font-weight: 600;
    letter-spacing: 0.22em;
    color: var(--copper-bright);
    padding: 0.55rem 0.9rem 0.8rem;

    .slashes {
      letter-spacing: 0.05em;
      color: rgba(242, 176, 94, 0.6);
    }
  }

  .viewport {
    position: relative;
    overflow: hidden;
    clip-path: polygon(14px 0, calc(100% - 14px) 0, 100% 14px, 100% calc(100% - 14px), calc(100% - 14px) 100%, 14px 100%, 0 calc(100% - 14px), 0 14px);

    img {
      display: block;
      width: 100%;
      height: clamp(280px, 30vw, 400px);
      object-fit: cover;
      filter: saturate(0.95);
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(180deg, transparent 60%, rgba(236, 233, 225, 0.55) 100%);
      pointer-events: none;
    }

    .badge {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      bottom: 1.3rem;
      z-index: 2;
      font-family: var(--font-mono);
      font-size: clamp(0.72rem, 1.3vw, 0.95rem);
      font-weight: 600;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--copper-bright);
      background: rgba(16, 13, 8, 0.82);
      border: 1px solid var(--copper);
      box-shadow: 0 0 18px rgba(224, 141, 60, 0.4), inset 0 0 14px rgba(224, 141, 60, 0.12);
      padding: 0.7rem 1.5rem;
      white-space: nowrap;
    }

    .credit {
      position: absolute;
      right: 0.9rem;
      bottom: 0.45rem;
      z-index: 2;
      font-family: var(--font-mono);
      font-size: 0.54rem;
      letter-spacing: 0.05em;
      color: rgba(34, 31, 26, 0.5);
    }
  }

  .chips {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.6rem;
    padding: 0.75rem 0.2rem 0.35rem;

    span {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      border: 1px solid var(--line-strong);
      background: rgba(255, 255, 255, 0.7);
      padding: 0.55rem 0.7rem;
      clip-path: polygon(8px 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0 50%);
      min-width: 0;
    }

    svg {
      flex: none;
      font-size: 0.85rem;
      color: var(--copper-bright);
    }

    b {
      display: block;
      font-family: var(--font-mono);
      font-size: 0.62rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--ink);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    i {
      display: block;
      font-style: normal;
      font-family: var(--font-mono);
      font-size: 0.52rem;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-faint);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  @media (max-width: 980px) {
    transform: none;

    &:hover {
      transform: none;
    }
  }

  @media (max-width: 560px) {
    .chips {
      grid-template-columns: 1fr;
    }
  }
`;

/* ============================== STATS ============================= */

const STAT_ICONS = {
  'Years of Excellence': <FaMedal />,
  Students: <FaUserGraduate />,
  'Faculty Members': <FaChalkboardTeacher />,
  'Research Projects': <FaFlask />,
};

const StatsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCell = styled.div`
  padding: clamp(2.2rem, 4.5vw, 3.4rem) 1.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: background 0.35s var(--ease-out);

  & + &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 20%;
    bottom: 20%;
    width: 1px;
    background: var(--line);
  }

  /* copper aura that breathes in on hover */
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(224, 141, 60, 0.16), transparent 65%);
    opacity: 0;
    transition: opacity 0.4s var(--ease-out);
    pointer-events: none;
  }

  &:hover::after {
    opacity: 1;
  }

  @media (max-width: 760px) {
    &:nth-child(3)::before {
      display: none;
    }
  }

  .glyph {
    width: 50px;
    height: 50px;
    margin: 0 auto 1.1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
    color: var(--copper-bright);
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(224, 141, 60, 0.45);
    clip-path: polygon(50% 0, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%);
    filter: drop-shadow(0 0 10px rgba(242, 176, 94, 0.35));
    transition: transform 0.3s var(--ease-out);
  }

  &:hover .glyph {
    transform: translateY(-3px) scale(1.06);
  }
`;

const StatValue = styled.div`
  font-family: var(--font-display);
  font-size: clamp(2.3rem, 5vw, 3.7rem);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  background: linear-gradient(180deg, var(--ink) 30%, rgba(34, 31, 26, 0.55) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;

  span {
    background: linear-gradient(115deg, var(--copper-bright), var(--copper-deep));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const StatLabel = styled.div`
  font-family: var(--font-mono);
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-top: 0.8rem;

  &::after {
    content: '';
    display: block;
    width: 30px;
    height: 2px;
    margin: 0.7rem auto 0;
    background: var(--copper);
    box-shadow: 0 0 8px rgba(224, 141, 60, 0.6);
  }
`;

function Stat({ value, suffix, label, started }) {
  const count = useCountUp(value, 1700, started);
  return (
    <StatCell>
      <span className="glyph" aria-hidden="true">
        {STAT_ICONS[label]}
      </span>
      <StatValue>
        {count.toLocaleString('en-IN')}
        <span>{suffix}</span>
      </StatValue>
      <StatLabel>{label}</StatLabel>
    </StatCell>
  );
}

function StatsBlock() {
  const [started, setStarted] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <StatsStrip ref={ref}>
      {STATS.map((s) => (
        <Stat key={s.label} {...s} started={started} />
      ))}
    </StatsStrip>
  );
}

/* ================= HIGHLIGHTS — 3D ORBITAL CAROUSEL ================ */

const highlightIcons = {
  talks: <FaMicrophone />,
  award: <FaTrophy />,
  wie: <FaUsers />,
  poster: <FaClipboardList />,
  industry: <FaIndustry />,
};

/* sample human-less imagery; CSS art beneath covers any load failure */
const highlightImages = {
  talks: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=900&q=70&auto=format',
  award: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=900&q=70&auto=format',
  wie: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=900&q=70&auto=format',
  poster: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=900&q=70&auto=format',
  industry: 'https://images.unsplash.com/photo-1535378620166-273708d44e4c?w=900&q=70&auto=format',
};

/* responsive ring dimensions: [cardW, cardH, radius] */
const HIGHLIGHT_DIMS = { wide: [430, 480, 460], mid: [340, 430, 360], small: [262, 370, 275] };

function useOrbitDims(dims) {
  const pick = () => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
    return w >= 1100 ? dims.wide : w >= 680 ? dims.mid : dims.small;
  };
  const [d, setD] = React.useState(pick);
  React.useEffect(() => {
    const onResize = () => setD(pick());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return d;
}

const Stage = styled.div`
  position: relative;
  height: ${(p) => p.$h + 84}px;
  margin-top: 2.5rem;
  perspective: 1500px;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: pan-y;

  /* glow floor under the ring */
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 8px;
    transform: translateX(-50%);
    width: min(${(p) => Math.round(p.$w * 1.9)}px, 86%);
    height: 60px;
    background: radial-gradient(ellipse at center, rgba(224, 141, 60, 0.22), transparent 70%);
    filter: blur(8px);
    pointer-events: none;
  }
`;

const Ring = styled.div`
  position: relative;
  width: ${(p) => p.$w}px;
  height: ${(p) => p.$h}px;
  transform-style: preserve-3d;
  transition: transform 0.85s var(--ease-out);
`;

const OrbitCard = styled.button`
  --cut: 18px;
  position: absolute;
  inset: 0;
  isolation: isolate;
  border: 0;
  padding: 0;
  background: ${(p) =>
    p.$active
      ? 'linear-gradient(110deg, #ffd28f 0%, #8d4713 22%, #ff9c2f 52%, #7c3c0c 80%, #ffc068 100%)'
      : 'linear-gradient(110deg, rgba(126, 132, 138, 0.5), rgba(44, 49, 56, 0.95) 30%, rgba(126, 132, 138, 0.4))'};
  clip-path: polygon(
    var(--cut) 0,
    calc(100% - 8px) 0,
    100% 8px,
    100% calc(100% - var(--cut)),
    calc(100% - var(--cut)) 100%,
    8px 100%,
    0 calc(100% - 8px),
    0 var(--cut)
  );
  cursor: pointer;
  transition: filter 0.5s var(--ease-out);
  filter: ${(p) => (p.$active ? 'brightness(1)' : 'brightness(0.42) saturate(0.75)')};

  .face {
    position: absolute;
    inset: 2px;
    clip-path: inherit;
    overflow: hidden;
    background: radial-gradient(130% 120% at 30% 0%, rgba(224, 141, 60, 0.25), #ffffff 55%, #ffffff 100%);
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.85;
  }

  .scrim {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(236, 233, 225, 0.05) 30%, rgba(236, 233, 225, 0.88) 82%);
  }

  .meta {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1.3rem 1.4rem 1.5rem;
    text-align: left;
  }

  /* face layout for the featured-tracks orbit */
  .track-face {
    position: relative;
    height: 100%;
    padding: 1.7rem 1.6rem;
    display: flex;
    flex-direction: column;
    text-align: left;

    h3 {
      font-size: 1.22rem;
      margin: 0.95rem 0 1rem;
    }

    ul {
      list-style: none;

      li {
        font-size: 0.92rem;
        color: var(--ink-dim);
        padding: 0.38rem 0;
        display: flex;
        align-items: baseline;
        gap: 0.55rem;

        &::before {
          content: '·';
          color: var(--copper-bright);
          font-weight: 700;
        }
      }
    }
  }

  .icon {
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.05rem;
    color: var(--copper-bright);
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(224, 141, 60, 0.5);
    clip-path: polygon(50% 0, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%);
    margin-bottom: 0.9rem;
  }

  h3 {
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--ink);
  }

  .index {
    position: absolute;
    top: 1.1rem;
    right: 1.2rem;
    font-family: var(--font-mono);
    font-size: 0.68rem;
    letter-spacing: 0.14em;
    color: var(--copper-bright);
    background: rgba(255, 255, 255, 0.75);
    padding: 0.3rem 0.7rem;
    border: 1px solid rgba(224, 141, 60, 0.35);
    border-radius: 999px;
  }
`;

const OrbitControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.3rem;
  margin-top: 0.6rem;

  > .arrow {
    flex: none;
  }

  .dots {
    display: flex;
    gap: 0.55rem;
  }

  .dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    border: 1px solid var(--line-strong);
    background: ${'transparent'};
    padding: 0;
    transition: all 0.25s;
    cursor: pointer;

    &.on {
      background: var(--copper);
      border-color: var(--copper);
      box-shadow: 0 0 10px rgba(224, 141, 60, 0.7);
    }
  }
`;

const OrbitCaption = styled.p`
  text-align: center;
  color: var(--ink-dim);
  font-size: 0.98rem;
  line-height: 1.7;
  max-width: 58ch;
  margin: 1.4rem auto 0;
  min-height: 3.4em;
`;

/*
 * Generic 360° orbit carousel — auto-rotates, arrows/dots, and swipe on
 * touch screens. `renderFace(item, i)` draws each card's contents.
 */
function OrbitCarousel({ items, dims, renderFace, caption, label }) {
  const count = items.length;
  const step = 360 / count;
  const [cardW, cardH, radius] = useOrbitDims(dims);
  const [active, setActive] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const pointerX = React.useRef(null);
  const dragged = React.useRef(false);

  React.useEffect(() => {
    if (paused) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = setInterval(() => setActive((a) => (a + 1) % count), 5200);
    return () => clearInterval(id);
  }, [paused, count]);

  /* drag / swipe to rotate — mouse and touch alike */
  const onPointerDown = (e) => {
    pointerX.current = e.clientX;
    dragged.current = false;
    setPaused(true);
  };

  const onPointerUp = (e) => {
    if (pointerX.current !== null) {
      const delta = e.clientX - pointerX.current;
      if (Math.abs(delta) > 40) {
        dragged.current = true;
        setActive((a) => (delta < 0 ? (a + 1) % count : (a - 1 + count) % count));
      }
    }
    pointerX.current = null;
    setPaused(false);
  };

  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <Stage
        $w={cardW}
        $h={cardH}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        style={{ cursor: 'grab' }}
      >
        <Ring
          $w={cardW}
          $h={cardH}
          style={{ transform: `translateZ(${-radius}px) rotateY(${-active * step}deg)` }}
        >
          {items.map((item, i) => (
            <OrbitCard
              key={item.id || item.title}
              $active={i === active}
              onClick={() => {
                if (!dragged.current) setActive(i);
              }}
              style={{ transform: `rotateY(${i * step}deg) translateZ(${radius}px)` }}
              aria-label={`${item.title} — show details`}
              aria-pressed={i === active}
            >
              {renderFace(item, i)}
            </OrbitCard>
          ))}
        </Ring>
      </Stage>

      <OrbitControls>
        <HudArrowButton
          className="arrow"
          onClick={() => setActive((a) => (a - 1 + count) % count)}
          aria-label={`Previous ${label}`}
        >
          <FiArrowLeft />
        </HudArrowButton>
        <div className="dots" role="tablist" aria-label={label}>
          {items.map((item, i) => (
            <button
              key={item.id || item.title}
              className={`dot ${i === active ? 'on' : ''}`}
              onClick={() => setActive(i)}
              role="tab"
              aria-selected={i === active}
              aria-label={item.title}
            />
          ))}
        </div>
        <HudArrowButton
          className="arrow"
          $accent
          onClick={() => setActive((a) => (a + 1) % count)}
          aria-label={`Next ${label}`}
        >
          <FiArrowRight />
        </HudArrowButton>
      </OrbitControls>

      {caption && <OrbitCaption aria-live="polite">{caption(items[active])}</OrbitCaption>}
    </div>
  );
}

/*
 * Panorama ring — the UX-India-style concave band: all twelve tracks on
 * the inside of a slowly revolving cylinder. Drag to spin, hover to pause.
 */
const PanoStage = styled.div`
  position: relative;
  height: 420px;
  margin-top: 2.6rem;
  perspective: 1050px;
  overflow: hidden;
  cursor: grab;
  touch-action: pan-y;

  &:active {
    cursor: grabbing;
  }

  /* edge fades so the band melts into the page */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 12%;
    z-index: 3;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(90deg, var(--bg), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(270deg, var(--bg), transparent);
  }

  @media (max-width: 640px) {
    height: 360px;
  }
`;

const PanoRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  transform-style: preserve-3d;
  will-change: transform;
`;

const PanoCard = styled.div`
  --cut: 13px;
  position: absolute;
  top: -160px;
  left: -120px;
  width: 240px;
  height: 320px;
  isolation: isolate;
  backface-visibility: hidden;
  background: linear-gradient(
    110deg,
    rgba(126, 132, 138, 0.5) 0%,
    rgba(44, 49, 56, 0.92) 22%,
    rgba(224, 141, 60, 0.55) 50%,
    rgba(44, 49, 56, 0.92) 80%,
    rgba(126, 132, 138, 0.45) 100%
  );
  clip-path: polygon(
    var(--cut) 0,
    calc(100% - 7px) 0,
    100% 7px,
    100% calc(100% - var(--cut)),
    calc(100% - var(--cut)) 100%,
    7px 100%,
    0 calc(100% - 7px),
    0 var(--cut)
  );
  padding: 1.3rem 1.2rem;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    inset: 1.5px;
    z-index: -1;
    background: radial-gradient(130% 120% at 50% 0%, rgba(224, 141, 60, 0.13), #ffffff 55%, #ffffff 100%);
    clip-path: inherit;
  }

  /* low-opacity thematic image behind the content */
  .bg {
    position: absolute;
    inset: 1.5px;
    width: calc(100% - 3px);
    height: calc(100% - 3px);
    object-fit: cover;
    opacity: 0.24;
    filter: saturate(0.85);
    pointer-events: none;
  }

  .num {
    position: relative;
    font-family: var(--font-mono);
    font-size: 0.64rem;
    letter-spacing: 0.12em;
    color: var(--copper-bright);
  }

  .symbol {
    position: relative;
    font-family: var(--font-display);
    font-size: 2.2rem;
    font-weight: 800;
    line-height: 1;
    color: rgba(34, 31, 26, 0.35);
    margin: 0.9rem 0 auto;
  }

  h3 {
    position: relative;
    font-size: 1.06rem;
    font-weight: 700;
    line-height: 1.35;
    margin-bottom: 0.7rem;
    text-shadow: 0 2px 8px rgba(236, 233, 225, 0.9);
  }

  .kw {
    position: relative;
    font-family: var(--font-mono);
    font-size: 0.58rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ink-dim);
  }

  @media (max-width: 640px) {
    top: -140px;
    left: -105px;
    width: 210px;
    height: 280px;
  }
`;

const TRACK_SYMBOLS = ['Sd', 'No', 'Ic', 'Vl', 'Mf', 'Re', 'Wc', 'Sp', 'Ai', 'Me', 'Mw', 'Rb'];

/* one thematic image per track — cards fall back to pure HUD art if any fail */
const U = (id) => `https://images.unsplash.com/photo-${id}?w=480&q=60&auto=format`;
const TRACK_IMAGES = [
  U('1518770660439-4636190af475'), // semiconductor — circuit macro
  U('1462331940025-496dfbfc7564'), // nano/opto — photonic nebula
  U('1558494949-ef010cbdcc31'), // industrial — server hall
  U('1461749280684-dccba630e2f6'), // vlsi — code & silicon
  U('1532094349884-543bc11b234d'), // micro-fab — laboratory
  U('1509391366360-2e959784a276'), // renewable — solar field
  U('1446776811953-b23d57bd21aa'), // wireless — satellite
  U('1518186285589-2f7649de83e0'), // signal — network abstract
  U('1620712943543-bcc4688e7485'), // ai/ml — machine intelligence
  U('1517077304055-6e89abbf09b0'), // mems — micro components
  U('1451187580459-43490279c0fa'), // microwave — global links
  U('1535378620166-273708d44e4c'), // robotics — automaton
];

function TracksPanorama() {
  const ringRef = React.useRef(null);
  const angle = React.useRef(0);
  const paused = React.useRef(false);
  const dragX = React.useRef(null);
  const count = TOPICS.length;
  const step = 360 / count;
  const radius = 470;

  React.useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!paused.current && !reduced) angle.current += dt * 5.5; // degrees / second
      if (ringRef.current) {
        ringRef.current.style.transform = `rotateY(${angle.current}deg)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onPointerDown = (e) => {
    dragX.current = e.clientX;
    paused.current = true;
  };

  const onPointerMove = (e) => {
    if (dragX.current === null) return;
    angle.current += (e.clientX - dragX.current) * 0.22;
    dragX.current = e.clientX;
  };

  const release = () => {
    dragX.current = null;
    paused.current = false;
  };

  return (
    <PanoStage
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={release}
      onPointerLeave={release}
      aria-label="Featured tracks panorama — drag to spin"
    >
      <PanoRing ref={ringRef}>
        {TOPICS.map((t, i) => (
          <PanoCard key={t.title} style={{ transform: `rotateY(${i * step}deg) translateZ(${-radius}px)` }}>
            <img
              className="bg"
              src={TRACK_IMAGES[i]}
              alt=""
              loading="lazy"
              draggable="false"
              onError={(e) => (e.currentTarget.style.display = 'none')}
            />
            <span className="num">/{String(i + 1).padStart(2, '0')}</span>
            <span className="symbol">{TRACK_SYMBOLS[i]}</span>
            <h3>{t.title}</h3>
            <span className="kw">{t.items[0]}</span>
          </PanoCard>
        ))}
      </PanoRing>
    </PanoStage>
  );
}

function HighlightsOrbit() {
  return (
    <OrbitCarousel
      items={HIGHLIGHTS}
      dims={HIGHLIGHT_DIMS}
      label="Highlights"
      caption={(item) => item.description}
      renderFace={(h, i) => (
        <span className="face">
          <img
            src={highlightImages[h.id]}
            alt=""
            loading="lazy"
            onError={(e) => (e.currentTarget.style.display = 'none')}
          />
          <span className="scrim" aria-hidden="true" />
          <span className="index">/{String(i + 1).padStart(2, '0')}</span>
          <span className="meta">
            <span className="icon">{highlightIcons[h.id]}</span>
            <h3>{h.title}</h3>
          </span>
        </span>
      )}
    />
  );
}

/* ========================= CALL FOR PAPERS ======================== */

const CfpPanel = styled.div`
  --cut: 30px;
  position: relative;
  isolation: isolate;
  background: linear-gradient(
    110deg,
    rgba(126, 132, 138, 0.55) 0%,
    rgba(44, 49, 56, 0.95) 16%,
    rgba(224, 141, 60, 0.9) 48%,
    rgba(42, 47, 54, 0.96) 80%,
    rgba(126, 132, 138, 0.55) 100%
  );
  clip-path: polygon(
    var(--cut) 0,
    calc(100% - var(--cut)) 0,
    100% var(--cut),
    100% calc(100% - var(--cut)),
    calc(100% - var(--cut)) 100%,
    var(--cut) 100%,
    0 calc(100% - var(--cut)),
    0 var(--cut)
  );
  filter: drop-shadow(0 22px 36px rgba(0, 0, 0, 0.45)) drop-shadow(0 0 20px rgba(224, 141, 60, 0.12));
  padding: clamp(2.2rem, 6vw, 4.5rem);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 2px;
    z-index: -1;
    background: radial-gradient(120% 160% at 80% -20%, rgba(224, 141, 60, 0.14) 0%, #ffffff 50%, #ffffff 100%);
    clip-path: inherit;
  }
`;

const GuidelineList = styled.ul`
  list-style: none;
  margin: 2rem 0 2.5rem;
  max-width: 70ch;

  li {
    position: relative;
    padding: 0.8rem 0 0.8rem 2.1rem;
    color: var(--ink-dim);
    line-height: 1.7;
    border-bottom: 1px dashed var(--line);

    &:last-child {
      border-bottom: none;
    }

    &::before {
      content: '◆';
      position: absolute;
      left: 0;
      top: 0.85rem;
      color: var(--copper);
      font-size: 0.7rem;
    }

    a {
      color: var(--copper-bright);
      border-bottom: 1px solid rgba(242, 176, 94, 0.35);
      transition: border-color 0.2s, color 0.2s;

      &:hover {
        color: var(--copper);
        border-color: var(--copper);
      }
    }

    .soon {
      color: var(--ink-faint);
      border-bottom: 1px dotted var(--ink-faint);
      cursor: not-allowed;
    }
  }
`;

const CmtAcknowledgement = styled.div`
  max-width: 70ch;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--line);
  color: var(--ink-dim);
  line-height: 1.7;

  h3 {
    color: var(--ink);
    font-size: 1rem;
    margin-bottom: 0.6rem;
  }
`;

/* ========================= FEATURED TOPICS ======================== */

const SeeAllLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-family: var(--font-mono);
  font-size: 0.8rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--copper-bright);
  margin-top: 2.4rem;
  transition: gap 0.25s var(--ease-out);

  &:hover {
    gap: 1rem;
  }
`;

/* =========================== CLOSING CTA ========================== */

const ClosingBand = styled.div`
  text-align: center;
  position: relative;
  padding: clamp(4rem, 9vw, 7rem) 0;
  overflow: hidden;
`;

export default function Home() {
  return (
    <>
      {/* ------------------------------ HERO ------------------------------ */}
      <Hero>
        <HeroPhoto aria-hidden="true" />
        <DotLattice />
        <CopperGlow style={{ top: '-220px', left: '50%', transform: 'translateX(-50%)' }} $size="900px" />
        <CopperGlow style={{ bottom: '-340px', right: '-200px' }} />
        <Orbital3D
          style={{ top: '50%', left: '50%', transform: 'translate(-50%, -54%)' }}
          opacity={0.5}
        />

        <HeroInner>
          <Reveal delay={80}>
            <HeroKicker>
              <b>10th</b> International Conference on Electronics,
              <br />
              Materials Engineering &amp; Nano-Technology
            </HeroKicker>
          </Reveal>

          <Reveal delay={140}>
            <HeroTitle>
              IEMENTech <span className="year">'27</span>
            </HeroTitle>
          </Reveal>

          <Reveal delay={200}>
            <TagRow>
              <span>
                <span className="tag">&lt;date&gt;</span> <span className="val">{CONFERENCE.dateLabel}</span>{' '}
                <span className="tag">&lt;/date&gt;</span>
              </span>
              <span>
                <span className="tag">&lt;venue&gt;</span> <span className="val">IIT Guwahati · Assam · India</span>{' '}
                <span className="tag">&lt;/venue&gt;</span>
              </span>
            </TagRow>
          </Reveal>

          <Reveal delay={260}>
            <Countdown />
          </Reveal>

          <Reveal delay={320}>
            <CtaRow>
              <ButtonPrimary as={Link} to="/submissions" $size="hero">
                Call for Papers <FiArrowRight aria-hidden="true" />
              </ButtonPrimary>
              <ButtonGhost as={Link} to="/registration" $size="hero">
                Registration Details <FiArrowRight aria-hidden="true" />
              </ButtonGhost>
            </CtaRow>
          </Reveal>

          <Reveal delay={380}>
            <OrganizerNote>
              Jointly organized by <strong>Dept. of ECE, Institute of Engineering &amp; Management, Kolkata</strong>{' '}
              and <strong>Indian Institute of Technology Guwahati</strong> · with technical co-sponsorship of{' '}
              <strong>IEEE Kolkata Section</strong>
            </OrganizerNote>
          </Reveal>
        </HeroInner>

        <ScrollCue aria-hidden="true">
          Scroll
          <span className="line" />
        </ScrollCue>
      </Hero>

      <Marquee
        filled
        items={[
          'Semiconductor Devices',
          'Nano-Materials',
          'VLSI & Embedded',
          'Renewable Energy',
          'AI · ML · IoT',
          'MEMS & Sensors',
          'Microwave & Antenna',
          'Signal Processing',
        ]}
      />

      {/* ---------------------------- OVERVIEW ---------------------------- */}
      <Section>
        <Container>
          <OverviewGrid>
            <Reveal>
              <div>
                <Kicker index="01">Overview</Kicker>
                <SectionTitle>
                  A decade of research, <em>three days</em> of convergence.
                </SectionTitle>

                <InfoPanel>
                  <span className="sigil" aria-hidden="true">
                    <FaAtom />
                  </span>
                  <p>
                    {CONFERENCE.fullName} (IEMENTech 2027) will be organized by Dept. of ECE, Institute of
                    Engineering &amp; Management, Kolkata with Technical Co-Sponsorship of IEEE Kolkata
                    Section at Indian Institute of Technology Guwahati (IIT Guwahati), Guwahati, Assam,
                    India during 2nd June to 4th June, 2027.
                  </p>
                </InfoPanel>

                <FactStrip>
                  <div className="fact">
                    <FaRegCalendarAlt aria-hidden="true" />
                    <div>
                      <b>10th</b>
                      <i>Edition</i>
                    </div>
                  </div>
                  <div className="fact">
                    <FaRegClock aria-hidden="true" />
                    <div>
                      <b>3 Days</b>
                      <i>of Convergence</i>
                    </div>
                  </div>
                  <div className="fact">
                    <FaUniversity aria-hidden="true" />
                    <div>
                      <b>IIT Guwahati</b>
                      <i>Venue</i>
                    </div>
                  </div>
                  <div className="fact">
                    <FaMapMarkerAlt aria-hidden="true" />
                    <div>
                      <b>Guwahati</b>
                      <i>Assam, India</i>
                    </div>
                  </div>
                </FactStrip>

                <InfoPanel>
                  <span className="sigil" aria-hidden="true">
                    <FaMicrochip />
                  </span>
                  <p>
                    The scientific deliberations — invited talks and contributory papers — will cover
                    diversified topics on Electronics, Materials Engineering, Nano Science and Technology.
                  </p>
                </InfoPanel>

                <ButtonGhost as="span" $disabled aria-disabled="true" style={{ marginTop: '1.6rem' }}>
                  <FaFilePdf aria-hidden="true" />
                  <span className="hide-sm">Conference&nbsp;</span>brochure — coming soon
                </ButtonGhost>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <ScreenScene>
                <Screen>
                  <div className="bar" aria-hidden="true">
                    <span>IEMENTECH 2027</span>
                    <span className="slashes">{'////'}</span>
                  </div>
                  <div className="viewport">
                    <img
                      src="/assets/iitg-campus.jpg"
                      alt="Administrative Building, Indian Institute of Technology Guwahati"
                    />
                    <span className="badge">Venue — IIT Guwahati</span>
                    <span className="credit">Photo: Giridhar Appaji Nag Y · CC BY 2.0</span>
                  </div>
                  <div className="chips">
                    <span>
                      <FaMapMarkerAlt aria-hidden="true" />
                      <span>
                        <b>Guwahati</b>
                        <i>Assam, India</i>
                      </span>
                    </span>
                    <span>
                      <FaMedal aria-hidden="true" />
                      <span>
                        <b>Excellence</b>
                        <i>in Research</i>
                      </span>
                    </span>
                    <span>
                      <FaBullseye aria-hidden="true" />
                      <span>
                        <b>Innovation</b>
                        <i>in Action</i>
                      </span>
                    </span>
                  </div>
                </Screen>
              </ScreenScene>
            </Reveal>
          </OverviewGrid>
        </Container>
      </Section>

      {/* ------------------------------ STATS ----------------------------- */}
      <StatsBlock />

      {/* --------------------------- HIGHLIGHTS --------------------------- */}
      <Section>
        <Container>
          <Reveal>
            <Kicker index="02">Conference Highlights</Kicker>
            <SectionTitle>
              Built for <em>exchange</em> — academia, industry, and beyond.
            </SectionTitle>
          </Reveal>
          <Reveal>
            <HighlightsOrbit />
          </Reveal>
        </Container>
      </Section>

      {/* ------------------------- CALL FOR PAPERS ------------------------ */}
      <Section $elevated $bordered>
        <Container>
          <Reveal>
            <CfpPanel>
              <DotLattice style={{ opacity: 0.5 }} />
              <div style={{ position: 'relative' }}>
                <Kicker index="03">Call for Papers</Kicker>
                <SectionTitle style={{ maxWidth: '22ch' }}>
                  Put your research on the <em>international stage.</em>
                </SectionTitle>
                <Lead>
                  Submit your research papers and be part of the premier international conference on Electronics,
                  Materials Engineering &amp; Nano-Technology. Join researchers and industry experts from around the
                  globe.
                </Lead>

                <GuidelineList>
                  <li>Original unpublished papers are invited as per the following guideline.</li>
                  <li>
                    Full paper, not exceeding 6 pages (including figures and tables), should be submitted in{' '}
                    <a
                      href="https://www.ieee.org/conferences/publishing/templates"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      IEEE format
                    </a>{' '}
                    through <span className="soon">CMT (opening soon)</span> on or before the deadline.
                  </li>
                </GuidelineList>

                <CtaRow style={{ justifyContent: 'flex-start' }}>
                  <ButtonPrimary as="span" $disabled aria-disabled="true">
                    Submit Paper — Soon
                  </ButtonPrimary>
                  <ButtonGhost
                    href="https://www.ieee.org/conferences/publishing/templates"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaFilePdf aria-hidden="true" /> IEEE Guidelines
                  </ButtonGhost>
                  <ButtonGhost as="span" $disabled aria-disabled="true">
                    <FaDownload aria-hidden="true" /> Brochure — Soon
                  </ButtonGhost>
                </CtaRow>
                <CmtAcknowledgement>
                  <h3>CMT Acknowledgement</h3>
                  <p>
                    The Microsoft CMT service was used for managing the peer-reviewing process for this
                    conference. This service was provided for free by Microsoft and they bore all expenses,
                    including costs for Azure cloud services as well as for software development and support.
                  </p>
                </CmtAcknowledgement>
              </div>
            </CfpPanel>
          </Reveal>
        </Container>
      </Section>

      {/* ------------------------- FEATURED TOPICS ------------------------ */}
      <Section>
        <Container>
          <Reveal>
            <Kicker index="04">Featured Tracks</Kicker>
            <SectionTitle>
              From the <em>nanoscale</em> to the network edge.
            </SectionTitle>
          </Reveal>
          <Reveal>
            <TracksPanorama />
          </Reveal>
          <Reveal>
            <SeeAllLink to="/topics" style={{ display: 'flex', justifyContent: 'center' }}>
              Explore all {TOPICS.length} conference topics <FaArrowRight aria-hidden="true" />
            </SeeAllLink>
          </Reveal>
        </Container>
      </Section>

      {/* ---------------------------- CLOSING CTA -------------------------- */}
      <Section $bordered style={{ padding: 0 }}>
        <ClosingBand>
          <DotLattice />
          <CopperGlow style={{ top: '-180px', left: '-160px' }} />
          <Container style={{ position: 'relative' }}>
            <Reveal>
              <Kicker style={{ justifyContent: 'center' }}>See you in Guwahati</Kicker>
              <SectionTitle style={{ margin: '0 auto 1.5rem', maxWidth: '20ch' }}>
                Three days on the banks of the <em>Brahmaputra.</em>
              </SectionTitle>
              <Lead style={{ margin: '0 auto 2.4rem' }}>
                {CONFERENCE.dateShort} · {CONFERENCE.venue}, {CONFERENCE.city}
              </Lead>
              <CtaRow>
                <ButtonPrimary as={Link} to="/registration">
                  Registration Details <FaArrowRight aria-hidden="true" />
                </ButtonPrimary>
                <ButtonGhost as={Link} to="/contact">
                  Contact Us
                </ButtonGhost>
              </CtaRow>
            </Reveal>
          </Container>
        </ClosingBand>
      </Section>
    </>
  );
}
